import { useState } from 'react';

import { useTranslation } from 'react-i18next';

import CheckCircleRoundedIcon from '@mui/icons-material/CheckCircleRounded';
import CloseRoundedIcon from '@mui/icons-material/CloseRounded';
import GppGoodRoundedIcon from '@mui/icons-material/GppGoodRounded';
import {
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';

import { CompactTablePagination, EmptyState, ErrorState, LoadingState, TablePageShell } from '@/shared/ui';

import { useApprovePrice, usePriceApprovals, useRejectPrice } from '../../hooks';
import { formatPrice } from '../../lib/format';
import type { GovernedPriceResponse } from '../../model/types';
import type { ToastSeverity } from '../../lib/useToast';

interface PanelProps {
  canManage: boolean;
  onResult: (severity: ToastSeverity, message: string) => void;
  /**
   * The card drawer's "Open approvals" deep link (design spec §8.3, `?tab=approvals&
   * productId=`) — there is no server-side filter on this endpoint (spec §3.1/Q5), so this only
   * drives a client-side row HIGHLIGHT over whatever page is loaded, plus the dismissible
   * "Product: {id}" chip below that names what was asked even when the row isn't on this page.
   */
  productId?: string | null;
  /** Dismisses the product-filter chip — clears `?productId=` on the page. */
  onClearProductFilter?: () => void;
}

/** Price-approval queue — prices held for review; approve (→ ACTIVE) or reject (→ REJECTED). */
export function PriceApprovalsPanel({ canManage, onResult, productId, onClearProductFilter }: PanelProps) {
  const { t } = useTranslation('catalog');
  const [page, setPage] = useState(0);
  const [size, setSize] = useState(20);
  const [rejectTarget, setRejectTarget] = useState<GovernedPriceResponse | null>(null);
  const [reason, setReason] = useState('');

  const query = usePriceApprovals({ page, size });
  const approve = useApprovePrice();
  const reject = useRejectPrice();
  const items = query.data?.content ?? [];

  const productFilterChip = productId ? (
    <Chip
      size="small"
      label={t('priceApprovalsPanel.productFilterChip', { id: productId })}
      onDelete={onClearProductFilter}
      deleteIcon={<CloseRoundedIcon titleAccess={t('priceApprovalsPanel.clearFilterAria')} />}
      sx={{ mb: 2, fontFamily: 'JetBrains Mono, monospace' }}
    />
  ) : null;

  if (query.isLoading) {
    return (
      <>
        {productFilterChip}
        <LoadingState label={t('priceApprovalsPanel.loading')} />
      </>
    );
  }
  if (query.isError) {
    return (
      <>
        {productFilterChip}
        <ErrorState
          title={t('priceApprovalsPanel.loadError.title')}
          message={query.error?.message ?? t('common:error.unknown')}
          onRetry={() => void query.refetch()}
        />
      </>
    );
  }
  if (items.length === 0) {
    return (
      <>
        {productFilterChip}
        <EmptyState
          title={t('priceApprovalsPanel.empty.title')}
          description={t('priceApprovalsPanel.empty.description')}
          icon={<GppGoodRoundedIcon />}
        />
      </>
    );
  }

  return (
    <>
      {productFilterChip}
      <TablePageShell
        footer={
          <CompactTablePagination
            count={query.data?.totalElements ?? 0}
            page={page}
            rowsPerPage={size}
            hideRowsPerPage
            onPageChange={(_e, next) => setPage(next)}
            onRowsPerPageChange={(e) => {
              setSize(parseInt(e.target.value, 10));
              setPage(0);
            }}
            labelDisplayedRows={({ from, to, count }) =>
              `${from}–${to} ${t('common:pagination.of')} ${count}`
            }
          />
        }
      >
        <TableContainer>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>{t('priceApprovalsPanel.table.product')}</TableCell>
                <TableCell align="right">{t('priceApprovalsPanel.table.price')}</TableCell>
                <TableCell align="right">{t('priceApprovalsPanel.table.previous')}</TableCell>
                <TableCell>{t('priceApprovalsPanel.table.status')}</TableCell>
                <TableCell>{t('priceApprovalsPanel.table.reason')}</TableCell>
                <TableCell align="right">{t('priceApprovalsPanel.table.actions')}</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {items.map((item) => (
                <TableRow key={item.id} hover selected={productId != null && item.productId === productId}>
                  <TableCell>
                    <Typography variant="caption" sx={{ fontFamily: 'JetBrains Mono, monospace' }}>
                      {item.productId}
                    </Typography>
                  </TableCell>
                  <TableCell align="right">{formatPrice(item.value)}</TableCell>
                  <TableCell align="right">{formatPrice(item.previousActiveValue)}</TableCell>
                  <TableCell>
                    <Chip
                      size="small"
                      variant="outlined"
                      color="warning"
                      label={item.priceStatus || item.state}
                    />
                  </TableCell>
                  <TableCell>
                    <Typography variant="caption" color="text.secondary">
                      {item.suspicionReason ?? item.reason ?? '—'}
                    </Typography>
                  </TableCell>
                  <TableCell align="right">
                    <Stack direction="row" spacing={1} justifyContent="flex-end">
                      <Button
                        size="small"
                        variant="outlined"
                        color="success"
                        startIcon={<CheckCircleRoundedIcon />}
                        disabled={!canManage || approve.isPending}
                        onClick={() =>
                          approve.mutate(item.id, {
                            onSuccess: () => onResult('success', t('priceApprovalsPanel.approved')),
                            onError: (error) => onResult('error', error.message),
                          })
                        }
                      >
                        {t('priceApprovalsPanel.approve')}
                      </Button>
                      <Button
                        size="small"
                        variant="text"
                        color="error"
                        disabled={!canManage}
                        onClick={() => {
                          setRejectTarget(item);
                          setReason('');
                        }}
                      >
                        {t('priceApprovalsPanel.reject')}
                      </Button>
                    </Stack>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      </TablePageShell>

      <Dialog
        open={rejectTarget !== null}
        onClose={() => setRejectTarget(null)}
        fullWidth
        maxWidth="xs"
      >
        <DialogTitle>{t('priceApprovalsPanel.rejectDialog.title')}</DialogTitle>
        <DialogContent>
          <TextField
            label={t('priceApprovalsPanel.rejectDialog.reasonLabel')}
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            fullWidth
            multiline
            minRows={2}
            sx={{ mt: 1 }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setRejectTarget(null)} disabled={reject.isPending}>
            {t('common:action.cancel')}
          </Button>
          <Button
            color="error"
            variant="contained"
            disabled={reject.isPending}
            onClick={() => {
              if (!rejectTarget) return;
              reject.mutate(
                { priceId: rejectTarget.id, reason: reason.trim() || undefined },
                {
                  onSuccess: () => {
                    setRejectTarget(null);
                    onResult('success', t('priceApprovalsPanel.rejected'));
                  },
                  onError: (error) => onResult('error', error.message),
                },
              );
            }}
          >
            {t('priceApprovalsPanel.reject')}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
}
